// src/pages/ShipmentCreateMulti.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { buildShipmentDraftFromPOs, createShipmentFromPOs } from "../services/shippingApi";

export default function ShipmentCreateMulti() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState({ carrier: "", container: "", trackingNo: "", note: "" });
  const [msg, setMsg] = useState("");

  const poCsv = params.get("po") || "";
  const poIds = useMemo(() => poCsv.split(",").map(s => s.trim()).filter(Boolean), [poCsv]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const draft = await buildShipmentDraftFromPOs(poIds);
        setLines(draft.lines ?? []);
      } catch (e) {
        setMsg("❌ " + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [poIds]);

  function setLineAt(i, patch) {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  }
  function removeLine(i) {
    setLines(prev => prev.filter((_, idx) => idx !== i));
  }

  const totalPlanned = useMemo(
    () => lines.reduce((s, l) => s + Number(l.qtyPlanned || 0) * Number(l.unitCost || 0), 0),
    [lines]
  );

  async function onCreateShipping() {
    try {
      setMsg("");
      const { shipmentId } = await createShipmentFromPOs({
        poIds,
        lines,
        meta: {
          ...meta,
          departAt: new Date().toISOString(), // ajuste si tu veux un champ date séparé
        }
      });
      setMsg("✅ Shipping créé");
      navigate(`/admin/shipments/${shipmentId}/receive`);
    } catch (e) {
      setMsg("❌ " + e.message);
    }
  }

  return (
    <div className="container">
      <h3 className="mb-3">Shipping groupé</h3>
      <div className="text-muted small mb-2">PO sélectionnés: {poIds.join(", ") || "—"}</div>

      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-12 col-md-3">
            <label className="form-label">Transporteur</label>
            <input className="form-control" value={meta.carrier} onChange={e=>setMeta(m=>({...m,carrier:e.target.value}))}/>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Conteneur / Ref</label>
            <input className="form-control" value={meta.container} onChange={e=>setMeta(m=>({...m,container:e.target.value}))}/>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">N° Tracking</label>
            <input className="form-control" value={meta.trackingNo} onChange={e=>setMeta(m=>({...m,trackingNo:e.target.value}))}/>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Note</label>
            <input className="form-control" value={meta.note} onChange={e=>setMeta(m=>({...m,note:e.target.value}))}/>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>PO</th>
                <th>Article</th>
                <th className="text-end">Restant</th>
                <th className="text-end">À shipper</th>
                <th className="text-end">Coût</th>
                <th className="text-end">Sous-total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}>Chargement…</td></tr>
              ) : lines.length ? (
                lines.map((l, i) => (
                  <tr key={`${l.poId}-${l.itemId}-${i}`}>
                    <td><code>{l.poId.slice(0,6)}</code></td>
                    <td>
                      <div className="fw-semibold">{l.name || l.itemId}</div>
                      <small className="text-muted">{l.itemId}</small>
                    </td>
                    <td className="text-end">{Number(l.remain||0).toLocaleString()}</td>
                    <td className="text-end" style={{maxWidth:120}}>
                      <input
                        type="number"
                        min={0}
                        max={l.remain || undefined}
                        className="form-control text-end"
                        value={l.qtyPlanned}
                        onChange={e=>setLineAt(i,{ qtyPlanned: Math.max(0, Math.min(Number(l.remain||0), Number(e.target.value||0))) })}
                      />
                    </td>
                    <td className="text-end">
                      <input
                        type="number"
                        min={0}
                        className="form-control text-end"
                        value={l.unitCost ?? 0}
                        onChange={e=>setLineAt(i,{ unitCost: Math.max(0, Number(e.target.value||0)) })}
                        style={{width:120}}
                      />
                    </td>
                    <td className="text-end">
                      {(Number(l.qtyPlanned||0)*Number(l.unitCost||0)).toLocaleString()}
                    </td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-danger" onClick={()=>removeLine(i)}>Retirer</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={7} className="text-muted">Aucune ligne à expédier</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex align-items-center gap-3 mt-3">
        <div className="ms-auto">
          Total planifié : <strong>{totalPlanned.toLocaleString()}</strong>
        </div>
        <button className="btn btn-dark" disabled={loading || !lines.length} onClick={onCreateShipping}>
          Créer le shipping
        </button>
      </div>

      {msg && <div className={"alert mt-3 " + (msg.startsWith("✅")?"alert-success":"alert-warning")}>{msg}</div>}

      <p className="small text-muted mt-3">
        Remarque : la création du shipping <em>n’altère pas</em> les PO. Les PO seront mis à jour lors de la
        <strong> réception </strong> du shipment.
      </p>
    </div>
  );
}
